import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  LinearProgress,
  Link,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Add as AddIcon,
  Close as CloseIcon,
  DeleteOutline as DeleteIcon,
  FileDownload as ExportIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";

import { tasksAPI } from "../../services/api";
import { wholesalersApi } from "../../services/api/omni/wholesalers";
import { wholesaleOrdersApi } from "../../services/api/omni/wholesaleOrders";
import { WH_ORDERS_CACHE_KEY, readCache, writeCache } from "./cache";
import {
  BAR_COLORS,
  CARRIERS,
  DELIVER_TO,
  DOT_COLORS,
  PAYMENT_KINDS,
  PAYMENT_METHODS,
  PAYMENT_MODELS,
  PHASES,
  PILL_SX,
  SEGMENTS,
  SHIP_METHODS,
  SHIP_STATUSES,
  buildPlanPreview,
  dueRelative,
  formatDate,
  formatMoney,
  formatMoneyShort,
  userDisplayName,
} from "./orderConstants";

const matchSegment = (item, segKey) => {
  const c = item.computed || {};
  const phase = c.phase;
  if (segKey === "all") return true;
  if (segKey === "open") return phase !== "completed";
  if (segKey === "due") return phase === "payment_due";
  if (segKey === "prepaid")
    return phase !== "completed" && Number(c.balance) > 0.005;
  if (segKey === "ship")
    return phase === "in_transit" || phase === "partially_delivered";
  if (segKey === "done") return phase === "completed";
  return true;
};

const computeSegCounts = (all) => {
  const counts = {};
  for (const seg of SEGMENTS) {
    counts[seg.key] = all.filter((i) => matchSegment(i, seg.key)).length;
  }
  return counts;
};

const TABLE_GRID =
  "minmax(180px, 1.4fr) 150px 108px 120px 120px 130px minmax(220px, 1.7fr)";

const useStyles = () => {
  const theme = useTheme();
  const dark = theme.palette.mode === "dark";
  return {
    dark,
    card: dark ? "#16203a" : "#ffffff",
    card2: dark ? "#1b2745" : "#f4f6fb",
    border: dark ? "#253254" : "#e3e8f2",
    line: dark ? "#2a3860" : "#e3e8f2",
    muted: dark ? "#9aa8c5" : "#6b7a94",
    text: dark ? "#e9eefb" : "#1a2336",
    rowHover: dark ? "#1e2a4a" : "#f0f4fb",
    blue: "#2f6fed",
    boxBg: dark ? "#131d35" : "#f7f9fc",
    footBg: dark ? "#131c31" : "#f5f7fb",
    accessBg: dark ? "#15224a" : "#eaf0ff",
    accessBorder: dark ? "#1d3f8f" : "#b7c9f5",
    barTrack: dark ? "#26345a" : "#dde3ef",
    btnBg: dark ? "#1b2745" : "#f4f6fb",
    btnHover: dark ? "#243257" : "#e8ecf4",
    nextStep: dark ? "#d4dcef" : "#3d4a63",
    theadBorder: dark ? "#1e2842" : "#e3e8f2",
    rowBorder: dark ? "#1a2440" : "#eef1f6",
    hot: "#ffaaa2",
    chipBg: dark ? "#1b2b56" : "#e8eef9",
    chipBorder: dark ? "#2c407a" : "#c5d4ef",
  };
};

const pillStyle = (tone, dark) => {
  const base = PILL_SX[tone] || PILL_SX.grey;
  if (dark) return base;
  const soft = {
    blue: { bg: "#e8f0ff", color: "#1e4a9e", border: "#b7c9f5" },
    amber: { bg: "#fff4e0", color: "#8a5a00", border: "#e8c878" },
    purple: { bg: "#f0ebff", color: "#5a3d9e", border: "#c9b8f0" },
    teal: { bg: "#e0f7fa", color: "#0d5c6e", border: "#8fd4e0" },
    grey: { bg: "#eef1f6", color: "#4a5568", border: "#c5cdd9" },
    red: { bg: "#ffeceb", color: "#a32a22", border: "#f0b0aa" },
    green: { bg: "#e6f9f0", color: "#0d6b45", border: "#8fd4b8" },
  };
  return soft[tone] || soft.grey;
};

const Pill = ({ tone = "grey", children, sx }) => {
  const s = useStyles();
  const p = pillStyle(tone, s.dark);
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        px: 1.25,
        py: 0.25,
        borderRadius: "99px",
        fontSize: 11.5,
        fontWeight: 600,
        whiteSpace: "nowrap",
        border: `1px solid ${p.border}`,
        background: p.bg,
        color: p.color,
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};

const ProgressBar = ({ value, fill = "blue" }) => {
  const s = useStyles();
  const color = BAR_COLORS[fill] || BAR_COLORS.blue;
  const pct = Math.min(100, Math.max(0, Number(value) || 0));
  return (
    <Box
      sx={{
        display: "block",
        height: 6,
        bgcolor: s.barTrack,
        borderRadius: 99,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          height: "100%",
          width: `${pct}%`,
          bgcolor: color,
          borderRadius: 99,
        }}
      />
    </Box>
  );
};

const StatDot = ({ color }) => (
  <Box
    component="span"
    sx={{
      width: 8,
      height: 8,
      borderRadius: "50%",
      bgcolor: color,
      display: "inline-block",
      mr: 0.85,
      verticalAlign: "middle",
      flexShrink: 0,
    }}
  />
);

const Field = ({ label, children, wide }) => {
  const s = useStyles();
  return (
    <Box sx={{ gridColumn: wide ? "1 / -1" : "auto" }}>
      <Typography
        sx={{
          fontSize: 11.5,
          textTransform: "uppercase",
          letterSpacing: 0.6,
          color: s.muted,
          fontWeight: 600,
          mb: 0.6,
        }}
      >
        {label}
      </Typography>
      {children}
    </Box>
  );
};

const inputSx = (s) => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: s.card2,
    borderRadius: "8px",
    fontSize: 13.5,
    minHeight: 40,
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: s.line,
  },
});

const outlineBtnSx = (s) => ({
  textTransform: "none",
  borderRadius: "8px",
  minHeight: 40,
  fontSize: 13,
  fontWeight: 500,
  border: `1px solid ${s.line}`,
  bgcolor: s.btnBg,
  color: s.text,
  boxShadow: "none",
  "&:hover": {
    bgcolor: s.btnHover,
    border: `1px solid ${s.line}`,
    boxShadow: "none",
  },
});

const primaryBtnSx = (s) => ({
  ...outlineBtnSx(s),
  bgcolor: s.blue,
  borderColor: s.blue,
  color: "#fff",
  fontWeight: 600,
  "&:hover": {
    bgcolor: "#3b7bf5",
    borderColor: "#3b7bf5",
    boxShadow: "none",
  },
});

const shipStatusPill = (status) => {
  if (status === "delivered") return "green";
  if (status === "problem") return "red";
  if (status === "pending") return "grey";
  return "blue";
};

const receivedBarFill = (o) => {
  const c = o.computed;
  const up = c?.received_pct ?? 0;
  if (up >= 100) return "green";
  if (Number(c?.balance) > 0.005 && c?.phase !== "completed") return "amber";
  return "blue";
};

const emptyPosition = () => ({
  ean: "",
  title: "",
  qty_ordered: "",
  unit_price: "",
  carrier: "",
  tracking_no: "",
  status: "pending",
});

const emptyCreate = () => ({
  wholesaler_id: "",
  order_no: "",
  order_date: new Date().toISOString().slice(0, 10),
  total_net: "",
  units_total: "",
  payment_model: "deposit",
  deposit_pct: "10",
  payment_term_days: "14",
  expected_parts: "2",
  expected_availability: "",
  pay_by: "",
  price_mode: "fixed",
  deliver_to: "prep_center",
  ship_method: "parcel",
  owner: "",
  note: "",
});

const SectionLabel = ({ children, s, first }) => (
  <Typography
    sx={{
      fontSize: 11,
      textTransform: "uppercase",
      letterSpacing: 0.9,
      color: s.dark ? "#7fa5ff" : "#2f6fed",
      fontWeight: 700,
      mt: first ? 0 : 2.75,
      mb: 1.25,
    }}
  >
    {children}
  </Typography>
);

const OrdersTab = () => {
  const s = useStyles();
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allItems, setAllItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [wholesalers, setWholesalers] = useState([]);
  const [users, setUsers] = useState([]);
  const [segment, setSegment] = useState("open");
  const [q, setQ] = useState("");
  const [fSup, setFSup] = useState("");

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(emptyCreate());
  const [createPositions, setCreatePositions] = useState([]);
  const [pendingFileId, setPendingFileId] = useState(null);
  const [parseFile, setParseFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [drawerTab, setDrawerTab] = useState(0);
  const [metaForm, setMetaForm] = useState({
    owner: "",
    deliver_to: "prep_center",
    invoice_no: "",
    expected_availability: "",
    pay_by: "",
    note: "",
  });
  const [savingMeta, setSavingMeta] = useState(false);

  const [payForm, setPayForm] = useState({
    amount: "",
    kind: "deposit",
    method: "bank_transfer",
    paid_at: new Date().toISOString().slice(0, 10),
  });
  const [editingPaymentId, setEditingPaymentId] = useState(null);
  const [payEditForm, setPayEditForm] = useState({
    amount: "",
    kind: "deposit",
    method: "bank_transfer",
    paid_at: "",
    reference: "",
  });
  const [itemForm, setItemForm] = useState(emptyPosition());

  const loadWholesalers = useCallback(async () => {
    try {
      const res = await wholesalersApi.list({
        status: "active",
        sort_by: "company_name",
      });
      setWholesalers(res.data.items || []);
    } catch (err) {
      console.error(err);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      const cached = localStorage.getItem("users");
      const ts = Number(localStorage.getItem("users_timestamp") || 0);
      if (cached && Date.now() - ts < 5 * 60 * 1000) {
        setUsers(JSON.parse(cached) || []);
        return;
      }
      const res = await tasksAPI.getUsers();
      const list = Array.isArray(res.data) ? res.data : res.data?.items || [];
      setUsers(list);
      localStorage.setItem("users", JSON.stringify(list));
      localStorage.setItem("users_timestamp", String(Date.now()));
    } catch (err) {
      console.error("Failed to load users", err);
    }
  }, []);

  const applyOrdersPayload = useCallback((all, summaryData) => {
    setAllItems(all);
    setSummary(summaryData);
    writeCache(WH_ORDERS_CACHE_KEY, { allItems: all, summary: summaryData });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [summaryRes, allRes] = await Promise.all([
        wholesaleOrdersApi.summary(),
        wholesaleOrdersApi.list({ segment: "all" }),
      ]);
      applyOrdersPayload(allRes.data.items || [], summaryRes.data);
    } catch (err) {
      setError(
        err?.response?.data?.detail || err.message || "Failed to load orders",
      );
    } finally {
      setLoading(false);
    }
  }, [applyOrdersPayload]);

  useEffect(() => {
    loadWholesalers();
    loadUsers();
  }, [loadWholesalers, loadUsers]);

  useEffect(() => {
    const cached = readCache(WH_ORDERS_CACHE_KEY);
    if (cached?.allItems && cached?.summary) {
      setAllItems(cached.allItems);
      setSummary(cached.summary);
      setLoading(false);
      return;
    }
    load();
  }, [load]);

  const segCounts = useMemo(() => computeSegCounts(allItems), [allItems]);

  const items = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return allItems.filter((o) => {
      if (!matchSegment(o, segment)) return false;
      if (fSup && String(o.wholesaler_id) !== String(fSup)) return false;
      if (!needle) return true;
      const hay = [
        o.wholesaler?.company_name,
        o.order_no,
        o.invoice_no,
        o.owner,
        o.note,
        ...(o.items || []).map((it) => it.tracking_no),
        ...(o.items || []).map((it) => it.title),
        ...(o.items || []).map((it) => it.ean),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(needle);
    });
  }, [allItems, segment, fSup, q]);

  const stats = useMemo(() => {
    if (!summary) return [];
    return [
      {
        dot: DOT_COLORS.blue,
        label: "Open orders",
        value: String(summary.open_count),
        sub: `Volume ${formatMoneyShort(summary.open_volume)}`,
      },
      {
        dot: DOT_COLORS.green,
        label: "Already paid",
        value: formatMoneyShort(summary.paid_on_open),
        sub: "on open orders",
      },
      {
        dot: DOT_COLORS.grey,
        label: "Still to pay",
        value: formatMoneyShort(summary.still_to_pay),
        sub: "across open orders",
      },
      {
        dot: DOT_COLORS.amber,
        label: "Prepaid, goods outstanding",
        value: formatMoneyShort(summary.prepaid_outstanding),
        sub: `at ${summary.prepaid_wholesaler_count} wholesaler(s)`,
      },
      {
        dot: DOT_COLORS.red,
        label: "Due within 7 days",
        value: formatMoneyShort(summary.due_in_7_days),
        sub: `${summary.due_in_7_count} payment(s)`,
      },
    ];
  }, [summary]);

  const openCreate = () => {
    const first = wholesalers[0];
    const me = users[0];
    setCreateForm({
      ...emptyCreate(),
      wholesaler_id: first ? String(first.id) : "",
      owner: me ? userDisplayName(me) : "",
    });
    setCreatePositions([]);
    setPendingFileId(null);
    setParseFile(null);
    setCreateOpen(true);
  };

  const planPreview = useMemo(() => buildPlanPreview(createForm), [createForm]);

  const syncMetaForm = (order) => {
    setMetaForm({
      owner: order?.owner || "",
      deliver_to: order?.deliver_to || "prep_center",
      invoice_no: order?.invoice_no || "",
      expected_availability: order?.expected_availability || "",
      pay_by: order?.pay_by ? String(order.pay_by).slice(0, 10) : "",
      note: order?.note || "",
    });
  };

  const openDrawer = async (row) => {
    try {
      const res = await wholesaleOrdersApi.get(row.id);
      setSelected(res.data);
      syncMetaForm(res.data);
      setDrawerTab(0);
      const dueKind = res.data.computed?.due_kind || "full";
      setPayForm({
        amount: res.data.computed?.due_amount
          ? String(res.data.computed.due_amount)
          : "",
        kind:
          dueKind === "deposit"
            ? "deposit"
            : dueKind === "balance"
              ? "balance"
              : "full",
        method: "bank_transfer",
        paid_at: new Date().toISOString().slice(0, 10),
      });
      setItemForm(emptyPosition());
      setDrawerOpen(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to open order");
    }
  };

  const refreshSelected = async (id) => {
    const res = await wholesaleOrdersApi.get(id);
    setSelected(res.data);
    syncMetaForm(res.data);
    await load();
  };

  const handleSaveMeta = async () => {
    if (!selected) return;
    setSavingMeta(true);
    setError(null);
    try {
      await wholesaleOrdersApi.update(selected.id, {
        owner: metaForm.owner || null,
        deliver_to: metaForm.deliver_to || "prep_center",
        invoice_no: metaForm.invoice_no.trim() || null,
        expected_availability: metaForm.expected_availability.trim() || null,
        pay_by: metaForm.pay_by || null,
        note: metaForm.note.trim() || null,
      });
      setSuccess("Order details saved");
      await refreshSelected(selected.id);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to save order details");
    } finally {
      setSavingMeta(false);
    }
  };

  const handleCreate = async () => {
    if (!createForm.wholesaler_id) {
      setError("Select an existing wholesaler from the directory");
      return;
    }
    if (
      !wholesalers.some(
        (w) => String(w.id) === String(createForm.wholesaler_id),
      )
    ) {
      setError("Orders can only be created for wholesalers that already exist");
      return;
    }
    if (!createForm.order_no.trim()) {
      setError("Order number is required");
      return;
    }
    if (!(Number(createForm.total_net) > 0)) {
      setError("Order value must be greater than 0");
      return;
    }
    if (
      createForm.owner &&
      !users.some((u) => userDisplayName(u) === createForm.owner)
    ) {
      setError("Owner must be selected from the users list");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const needsPayBy = ["deposit", "prepaid", "partial_shipment"].includes(
        createForm.payment_model,
      );
      let payBy = createForm.pay_by || undefined;
      if (needsPayBy && !payBy) {
        const d = new Date();
        d.setDate(d.getDate() + 3);
        payBy = d.toISOString().slice(0, 10);
      }
      let note = createForm.note || "";
      if (createForm.payment_model === "preorder" && createForm.price_mode) {
        const priceLabel =
          createForm.price_mode === "spot"
            ? "Spot price on availability"
            : "Fixed price";
        note = note ? `${note}\n${priceLabel}` : priceLabel;
      }
      const items = createPositions
        .filter((p) => (p.title || "").trim())
        .map((p) => ({
          ean: p.ean?.trim() || undefined,
          title: p.title.trim(),
          qty_ordered: Number(p.qty_ordered) || 0,
          unit_price: p.unit_price !== "" ? Number(p.unit_price) : undefined,
          carrier: p.carrier || undefined,
          tracking_no: p.tracking_no?.trim() || undefined,
          status: p.status || "pending",
          units: Number(p.qty_ordered) || 0,
        }));
      const unitsFromItems = items.reduce(
        (sum, it) => sum + (Number(it.qty_ordered) || 0),
        0,
      );
      const payload = {
        wholesaler_id: Number(createForm.wholesaler_id),
        order_no: createForm.order_no.trim(),
        order_date: createForm.order_date || undefined,
        total_net: Number(createForm.total_net),
        units_total: Number(createForm.units_total) || unitsFromItems || 0,
        payment_model: createForm.payment_model,
        deposit_pct:
          createForm.payment_model === "deposit"
            ? Number(createForm.deposit_pct) || 10
            : undefined,
        payment_term_days:
          createForm.payment_model === "net_terms"
            ? Number(createForm.payment_term_days) || 14
            : undefined,
        expected_parts:
          createForm.payment_model === "partial_shipment"
            ? Number(createForm.expected_parts) || 2
            : undefined,
        expected_availability: createForm.expected_availability || undefined,
        pay_by: payBy,
        deliver_to: createForm.deliver_to,
        ship_method: createForm.ship_method || undefined,
        owner: createForm.owner || undefined,
        note: note || undefined,
        items,
        pending_file_id: pendingFileId || undefined,
      };
      await wholesaleOrdersApi.create(payload);
      setCreateOpen(false);
      setSuccess("Order created");
      setSegment("all");
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "Create failed");
    } finally {
      setSaving(false);
    }
  };

  const handleParseFile = async () => {
    if (!parseFile) {
      setError("Choose a file to upload first");
      return;
    }
    const wh = wholesalers.find(
      (w) => String(w.id) === String(createForm.wholesaler_id),
    );
    if (!wh) {
      setError("Select a wholesaler before parsing the file");
      return;
    }
    setParsing(true);
    setError(null);
    try {
      const res = await wholesaleOrdersApi.parseFile(
        parseFile,
        wh.company_name,
        wh.id,
      );
      setPendingFileId(res.data.pending_file_id);
      const rows = (res.data.items || []).map((it) => ({
        ean: it.ean || "",
        title: it.title || "",
        qty_ordered: it.qty_ordered != null ? String(it.qty_ordered) : "",
        unit_price: it.unit_price != null ? String(it.unit_price) : "",
        carrier: it.carrier || "",
        tracking_no: it.tracking_no || "",
        status: it.status || "pending",
      }));
      setCreatePositions(rows);

      const unitsFromItems = rows.reduce(
        (sum, r) => sum + (Number(r.qty_ordered) || 0),
        0,
      );
      setCreateForm((p) => {
        const next = { ...p };
        if (res.data.order_number) {
          next.order_no = String(res.data.order_number);
        }
        if (res.data.order_date) {
          next.order_date = String(res.data.order_date).slice(0, 10);
        }
        if (
          res.data.order_value_net != null &&
          res.data.order_value_net !== ""
        ) {
          next.total_net = String(res.data.order_value_net);
        }
        if (res.data.total_units != null && res.data.total_units !== "") {
          next.units_total = String(res.data.total_units);
        } else if (!p.units_total && unitsFromItems) {
          next.units_total = String(unitsFromItems);
        }
        return next;
      });
      setSuccess(`Parsed ${rows.length} position(s) from file`);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "Parse failed");
    } finally {
      setParsing(false);
    }
  };

  const handleDelete = async () => {
    if (!selected) return;
    const label = `${selected.wholesaler?.company_name || "Order"} · ${
      selected.order_no
    }`;
    if (!window.confirm(`Delete order ${label}? This cannot be undone.`)) {
      return;
    }
    setDeleting(true);
    setError(null);
    try {
      await wholesaleOrdersApi.remove(selected.id);
      setDrawerOpen(false);
      setSelected(null);
      setSuccess("Order deleted");
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  const handleAddPayment = async () => {
    if (!selected) return;
    if (!(Number(payForm.amount) > 0)) {
      setError("Enter a payment amount");
      return;
    }
    try {
      await wholesaleOrdersApi.addPayment(selected.id, {
        amount: Number(payForm.amount),
        kind: payForm.kind,
        method: payForm.method,
        paid_at: payForm.paid_at || undefined,
      });
      setSuccess("Payment saved");
      await refreshSelected(selected.id);
    } catch (err) {
      setError(err?.response?.data?.detail || "Payment failed");
    }
  };

  const startEditPayment = (p) => {
    setEditingPaymentId(p.id);
    setPayEditForm({
      amount: String(p.amount ?? ""),
      kind: p.kind || "deposit",
      method: p.method || "bank_transfer",
      paid_at: p.paid_at ? String(p.paid_at).slice(0, 10) : "",
      reference: p.reference || "",
    });
  };

  const handleUpdatePayment = async () => {
    if (!selected || !editingPaymentId) return;
    if (!(Number(payEditForm.amount) > 0)) {
      setError("Enter a payment amount");
      return;
    }
    try {
      await wholesaleOrdersApi.updatePayment(selected.id, editingPaymentId, {
        amount: Number(payEditForm.amount),
        kind: payEditForm.kind,
        method: payEditForm.method,
        paid_at: payEditForm.paid_at || undefined,
        reference: payEditForm.reference?.trim() || null,
      });
      setEditingPaymentId(null);
      setSuccess("Payment updated");
      await refreshSelected(selected.id);
    } catch (err) {
      setError(err?.response?.data?.detail || "Payment update failed");
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!selected) return;
    if (!window.confirm("Delete this payment?")) return;
    try {
      await wholesaleOrdersApi.removePayment(selected.id, paymentId);
      if (editingPaymentId === paymentId) setEditingPaymentId(null);
      setSuccess("Payment deleted");
      await refreshSelected(selected.id);
    } catch (err) {
      setError(err?.response?.data?.detail || "Payment delete failed");
    }
  };

  const patchItem = async (itemId, payload) => {
    if (!selected) return;
    try {
      await wholesaleOrdersApi.updateItem(selected.id, itemId, payload);
      await refreshSelected(selected.id);
    } catch (err) {
      setError(err?.response?.data?.detail || "Update failed");
    }
  };

  const handleDeleteItem = async (itemId, title) => {
    if (!selected) return;
    if (
      !window.confirm(
        `Delete position${title ? ` “${title}”` : ""}? This cannot be undone.`,
      )
    ) {
      return;
    }
    try {
      await wholesaleOrdersApi.removeItem(selected.id, itemId);
      setSuccess("Position deleted");
      await refreshSelected(selected.id);
    } catch (err) {
      setError(err?.response?.data?.detail || "Position delete failed");
    }
  };

  const handleAddItem = async () => {
    if (!selected) return;
    if (!(itemForm.title || "").trim()) {
      setError("Position title is required");
      return;
    }
    try {
      await wholesaleOrdersApi.addItem(selected.id, {
        title: itemForm.title.trim(),
        ean: itemForm.ean?.trim() || undefined,
        qty_ordered: Number(itemForm.qty_ordered) || 0,
        unit_price:
          itemForm.unit_price !== "" ? Number(itemForm.unit_price) : undefined,
        carrier: itemForm.carrier || undefined,
        tracking_no: itemForm.tracking_no?.trim() || undefined,
        status: itemForm.status || "pending",
        units: Number(itemForm.qty_ordered) || 0,
      });
      setItemForm(emptyPosition());
      setSuccess("Position saved");
      await refreshSelected(selected.id);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to add position");
    }
  };

  const handleDownloadInvoice = async () => {
    if (!selected?.has_invoice_file) return;
    try {
      const res = await wholesaleOrdersApi.downloadInvoiceFile(selected.id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `order-${selected.order_no}-invoice`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err?.response?.data?.detail || "Download failed");
    }
  };

  const exportCsv = () => {
    const header = [
      "wholesaler",
      "order_no",
      "order_date",
      "payment_model",
      "total_net",
      "paid",
      "still_to_pay",
      "phase",
      "currency",
    ];
    const lines = [header.join(",")];
    for (const o of items) {
      lines.push(
        [
          JSON.stringify(o.wholesaler?.company_name || ""),
          JSON.stringify(o.order_no),
          o.order_date,
          o.payment_model,
          o.total_net,
          o.computed?.paid,
          o.computed?.unpaid,
          o.computed?.phase,
          o.currency,
        ].join(","),
      );
    }
    const blob = new Blob([lines.join("\n")], {
      type: "text/csv;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "wholesale_orders.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const c = selected?.computed;

  const drawerTabSx = {
    minHeight: 42,
    textTransform: "none",
    fontSize: 13,
    color: s.muted,
    "&.Mui-selected": {
      color: s.text,
      fontWeight: 600,
    },
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", md: "flex-end" }}
        spacing={2}
        mb={2.25}
      >
        <Box>
          <Typography
            sx={{
              fontSize: { xs: 24, md: 30 },
              fontWeight: 600,
              letterSpacing: "-0.4px",
              color: s.text,
            }}
          >
            Orders &amp; payments
          </Typography>
          <Typography
            sx={{ color: s.muted, fontSize: 13, mt: 0.6, maxWidth: 680 }}
          >
            Track each wholesaler order: payment model, amounts paid, goods
            received, and shipment tracking. Orders can only be created for
            existing wholesalers.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.1} flexWrap="wrap">
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={load}
            disabled={loading}
            sx={outlineBtnSx(s)}
          >
            {loading ? "Loading…" : "Reload"}
          </Button>
          <Button
            variant="outlined"
            startIcon={<ExportIcon />}
            onClick={exportCsv}
            disabled={!items.length}
            sx={outlineBtnSx(s)}
          >
            Export CSV
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreate}
            disabled={!wholesalers.length}
            sx={primaryBtnSx(s)}
          >
            New order
          </Button>
        </Stack>
      </Stack>

      {!wholesalers.length && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Add a wholesaler in the Directory tab before creating orders.
        </Alert>
      )}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
          gap: 1.5,
          mb: 2.25,
        }}
      >
        {stats.map((st) => (
          <Box
            key={st.label}
            sx={{
              background: s.card,
              borderRadius: "10px",
              border: `1px solid ${s.border}`,
              p: "14px 16px",
            }}
          >
            <Typography
              component="div"
              sx={{
                fontSize: 11.5,
                textTransform: "uppercase",
                letterSpacing: 0.6,
                color: s.muted,
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
              }}
            >
              <StatDot color={st.dot} />
              {st.label}
            </Typography>
            <Typography
              sx={{
                fontSize: 24,
                fontWeight: 600,
                mt: 0.75,
                lineHeight: 1.15,
                fontVariantNumeric: "tabular-nums",
                color: s.text,
              }}
            >
              {st.value}
            </Typography>
            <Typography sx={{ fontSize: 12, color: s.muted, mt: 0.4 }}>
              {st.sub}
            </Typography>
          </Box>
        ))}
      </Box>

      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={1.25}
        alignItems={{ md: "center" }}
        mb={1.75}
        flexWrap="wrap"
      >
        <Box
          sx={{
            display: "flex",
            gap: 0.25,
            flexWrap: "wrap",
            background: s.card,
            borderRadius: "9px",
            p: 0.4,
            border: `1px solid ${s.border}`,
          }}
        >
          {SEGMENTS.map((seg) => {
            const active = segment === seg.key;
            return (
              <Button
                key={seg.key}
                size="small"
                onClick={() => setSegment(seg.key)}
                sx={{
                  borderRadius: "7px",
                  textTransform: "none",
                  minHeight: 36,
                  px: 1.5,
                  fontSize: 13,
                  fontWeight: active ? 600 : 500,
                  color: active ? "#fff" : s.muted,
                  bgcolor: active ? s.blue : "transparent",
                  boxShadow: "none",
                  "&:hover": {
                    bgcolor: active ? s.blue : "transparent",
                    color: active ? "#fff" : s.text,
                    boxShadow: "none",
                  },
                }}
              >
                {seg.label}
                <Box
                  component="span"
                  sx={{
                    ml: 0.85,
                    px: 0.85,
                    borderRadius: 99,
                    fontSize: 11,
                    fontWeight: 600,
                    background: active
                      ? "rgba(255,255,255,0.13)"
                      : s.dark
                        ? "rgba(255,255,255,0.06)"
                        : "rgba(0,0,0,0.06)",
                  }}
                >
                  {segCounts[seg.key] ?? 0}
                </Box>
              </Button>
            );
          })}
        </Box>
        <TextField
          size="small"
          placeholder="Search wholesaler, order no, tracking…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          sx={{ ...inputSx(s), flex: 1, minWidth: 200 }}
        />
        <TextField
          select
          size="small"
          label="Wholesaler"
          value={fSup}
          onChange={(e) => setFSup(e.target.value)}
          sx={{ ...inputSx(s), minWidth: 200, flex: "0 1 200px" }}
        >
          <MenuItem value="">All</MenuItem>
          {wholesalers.map((w) => (
            <MenuItem key={w.id} value={String(w.id)}>
              {w.company_name}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      <Box
        sx={{
          background: s.card,
          borderRadius: "10px",
          border: `1px solid ${s.border}`,
          overflowX: "auto",
        }}
      >
        {loading ? (
          <LinearProgress sx={{ borderRadius: 0 }} />
        ) : items.length === 0 ? (
          <Box sx={{ p: 5.5, textAlign: "center", color: s.muted }}>
            <Typography
              sx={{ color: s.text, fontWeight: 600, mb: 0.75, fontSize: 15 }}
            >
              No orders found
            </Typography>
            Try another segment or create a new order.
          </Box>
        ) : (
          <>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: TABLE_GRID,
                gap: 2,
                alignItems: "center",
                px: 2.5,
                py: 1.6,
                minWidth: 1180,
                color: s.muted,
                fontSize: 11.5,
                textTransform: "uppercase",
                letterSpacing: 0.7,
                fontWeight: 600,
                borderBottom: `1px solid ${s.theadBorder}`,
              }}
            >
              <div>Order</div>
              <div>Payment model</div>
              <div>Order value</div>
              <div>Paid</div>
              <div>Still to pay</div>
              <div>Goods received</div>
              <div>Status &amp; next step</div>
            </Box>
            {items.map((o) => {
              const phase =
                PHASES[o.computed?.phase] || PHASES.waiting_for_goods;
              const modelMeta = PAYMENT_MODELS[o.payment_model];
              const model = modelMeta || {
                short: o.payment_model,
                pill: "grey",
                sub: () => "",
              };
              const paidPct = o.computed?.paid_pct ?? 0;
              const recvPct = o.computed?.received_pct ?? 0;
              const dueInfo =
                o.computed?.phase === "payment_due"
                  ? dueRelative(o.pay_by)
                  : null;

              return (
                <Box
                  key={o.id}
                  component="button"
                  type="button"
                  onClick={() => openDrawer(o)}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: TABLE_GRID,
                    gap: 2,
                    alignItems: "center",
                    px: 2.5,
                    py: 1.6,
                    minWidth: 1180,
                    width: "100%",
                    border: "none",
                    borderBottom: `1px solid ${s.rowBorder}`,
                    cursor: "pointer",
                    textAlign: "left",
                    color: s.text,
                    bgcolor: "transparent",
                    font: "inherit",
                    "&:hover": { background: s.rowHover },
                  }}
                >
                  <Box>
                    <Typography
                      sx={{ fontWeight: 600, color: s.text, fontSize: 13.8 }}
                    >
                      {o.wholesaler?.company_name ||
                        `Wholesaler #${o.wholesaler_id}`}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: s.muted, mt: 0.25 }}>
                      {o.order_no} · {formatDate(o.order_date)}
                    </Typography>
                  </Box>
                  <Box>
                    <Pill tone={model.pill || "grey"}>{model.short}</Pill>
                    <Typography
                      sx={{ fontSize: 11.5, color: s.muted, mt: 0.5 }}
                    >
                      {typeof model.sub === "function" ? model.sub(o) : ""}
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {formatMoney(o.total_net, o.currency)}
                  </Typography>
                  <Box>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      mb={0.6}
                    >
                      <Typography
                        sx={{
                          fontSize: 12,
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {formatMoney(o.computed?.paid, o.currency)}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 12,
                          color: s.muted,
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {paidPct}%
                      </Typography>
                    </Stack>
                    <ProgressBar
                      value={paidPct}
                      fill={paidPct >= 100 ? "green" : "blue"}
                    />
                  </Box>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontVariantNumeric: "tabular-nums",
                      color:
                        Number(o.computed?.unpaid) > 0.005
                          ? s.hot
                          : s.muted,
                    }}
                  >
                    {formatMoney(o.computed?.unpaid ?? 0, o.currency)}
                  </Typography>
                  <Box>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      mb={0.6}
                    >
                      <Typography
                        sx={{
                          fontSize: 12,
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {o.units_total
                          ? `${o.computed?.units_in || 0} / ${o.units_total}`
                          : formatMoney(
                              o.computed?.delivered_value,
                              o.currency,
                            )}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 12,
                          color: s.muted,
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {recvPct}%
                      </Typography>
                    </Stack>
                    <ProgressBar value={recvPct} fill={receivedBarFill(o)} />
                  </Box>
                  <Box>
                    <Pill tone={phase.pill || "grey"}>
                      {o.computed?.phase_label || phase.label}
                    </Pill>
                    <Typography
                      sx={{
                        fontSize: 12.5,
                        mt: 0.6,
                        color: s.nextStep,
                        display: "block",
                      }}
                    >
                      {o.computed?.next_step}
                    </Typography>
                    {dueInfo && (
                      <Typography
                        sx={{
                          fontSize: 11.5,
                          mt: 0.25,
                          display: "block",
                          fontWeight: 600,
                          color: dueInfo.hot ? s.hot : s.muted,
                        }}
                      >
                        {dueInfo.text}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
          </>
        )}
      </Box>

      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "14px",
            border: `1px solid ${s.line}`,
            bgcolor: s.card,
            maxHeight: "calc(100% - 48px)",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontSize: 19,
            fontWeight: 600,
            pb: 0.5,
            pr: 6,
            position: "relative",
          }}
        >
          Create order
          <IconButton
            onClick={() => setCreateOpen(false)}
            sx={{ position: "absolute", top: 8, right: 8, color: s.muted }}
            aria-label="Close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography sx={{ color: s.muted, fontSize: 12.5, mb: 0.5 }}>
            Required: existing wholesaler, order number, order value and payment
            model. Upload an invoice/order file to parse positions via n8n, or
            add rows manually.
          </Typography>

          <SectionLabel s={s} first>
            Order
          </SectionLabel>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 1.5,
            }}
          >
            <Field label="Wholesaler *">
              <TextField
                select
                fullWidth
                size="small"
                value={createForm.wholesaler_id}
                onChange={(e) =>
                  setCreateForm((p) => ({
                    ...p,
                    wholesaler_id: e.target.value,
                  }))
                }
                sx={inputSx(s)}
                disabled={!wholesalers.length}
              >
                {!wholesalers.length && (
                  <MenuItem value="" disabled>
                    No wholesalers in directory
                  </MenuItem>
                )}
                {wholesalers.map((w) => (
                  <MenuItem key={w.id} value={String(w.id)}>
                    {w.company_name}
                  </MenuItem>
                ))}
              </TextField>
              <Typography sx={{ mt: 0.5, fontSize: 11.5, color: s.muted }}>
                Only wholesalers already in the Directory can be selected.
              </Typography>
            </Field>
            <Field label="Wholesaler order number *">
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. NT-24901"
                value={createForm.order_no}
                onChange={(e) =>
                  setCreateForm((p) => ({ ...p, order_no: e.target.value }))
                }
                sx={inputSx(s)}
              />
            </Field>
            <Field label="Order date">
              <TextField
                type="date"
                fullWidth
                size="small"
                value={createForm.order_date}
                onChange={(e) =>
                  setCreateForm((p) => ({ ...p, order_date: e.target.value }))
                }
                sx={inputSx(s)}
                InputLabelProps={{ shrink: true }}
              />
            </Field>
            <Field label="Order value net (€) *">
              <TextField
                type="number"
                fullWidth
                size="small"
                placeholder="0.00"
                value={createForm.total_net}
                onChange={(e) =>
                  setCreateForm((p) => ({ ...p, total_net: e.target.value }))
                }
                sx={inputSx(s)}
              />
            </Field>
            <Field label="Total units">
              <TextField
                type="number"
                fullWidth
                size="small"
                placeholder="0"
                value={createForm.units_total}
                onChange={(e) =>
                  setCreateForm((p) => ({ ...p, units_total: e.target.value }))
                }
                sx={inputSx(s)}
              />
            </Field>
            <Field label="Owner">
              <TextField
                select
                fullWidth
                size="small"
                value={createForm.owner}
                onChange={(e) =>
                  setCreateForm((p) => ({ ...p, owner: e.target.value }))
                }
                sx={inputSx(s)}
                disabled={!users.length}
              >
                {!users.length && (
                  <MenuItem value="" disabled>
                    No users available
                  </MenuItem>
                )}
                {users.map((u) => {
                  const name = userDisplayName(u);
                  return (
                    <MenuItem key={u.id || name} value={name}>
                      {name}
                    </MenuItem>
                  );
                })}
              </TextField>
              <Typography sx={{ mt: 0.5, fontSize: 11.5, color: s.muted }}>
                Pick from dashboard users only.
              </Typography>
            </Field>
          </Box>

          <SectionLabel s={s}>How is it paid?</SectionLabel>
          <Box
            sx={{
              border: `1px solid ${s.accessBorder}`,
              background: s.accessBg,
              borderRadius: "10px",
              p: 1.75,
            }}
          >
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {Object.entries(PAYMENT_MODELS).map(([key, meta]) => {
                const on = createForm.payment_model === key;
                return (
                  <Button
                    key={key}
                    onClick={() =>
                      setCreateForm((p) => ({ ...p, payment_model: key }))
                    }
                    sx={{
                      textTransform: "none",
                      borderRadius: 99,
                      minHeight: 38,
                      px: 1.75,
                      fontSize: 12.5,
                      fontWeight: on ? 600 : 500,
                      color: on ? "#fff" : s.dark ? "#dbe4f7" : s.text,
                      bgcolor: on ? s.blue : s.chipBg,
                      border: `1px solid ${on ? s.blue : s.chipBorder}`,
                      boxShadow: "none",
                      "&:hover": {
                        bgcolor: on ? "#3b7bf5" : s.btnHover,
                        boxShadow: "none",
                      },
                    }}
                  >
                    {meta.label}
                  </Button>
                );
              })}
            </Stack>
            <Typography sx={{ mt: 1.25, fontSize: 12.5, color: s.muted }}>
              {PAYMENT_MODELS[createForm.payment_model]?.desc}
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 1.5,
                mt: 1.5,
              }}
            >
              {createForm.payment_model === "deposit" && (
                <>
                  <Field label="Deposit %">
                    <TextField
                      type="number"
                      fullWidth
                      size="small"
                      value={createForm.deposit_pct}
                      onChange={(e) =>
                        setCreateForm((p) => ({
                          ...p,
                          deposit_pct: e.target.value,
                        }))
                      }
                      sx={inputSx(s)}
                    />
                  </Field>
                  <Field label="Deposit due by">
                    <TextField
                      type="date"
                      fullWidth
                      size="small"
                      value={createForm.pay_by}
                      onChange={(e) =>
                        setCreateForm((p) => ({ ...p, pay_by: e.target.value }))
                      }
                      sx={inputSx(s)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Field>
                </>
              )}
              {createForm.payment_model === "prepaid" && (
                <Field label="Payment due by">
                  <TextField
                    type="date"
                    fullWidth
                    size="small"
                    value={createForm.pay_by}
                    onChange={(e) =>
                      setCreateForm((p) => ({ ...p, pay_by: e.target.value }))
                    }
                    sx={inputSx(s)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Field>
              )}
              {createForm.payment_model === "partial_shipment" && (
                <>
                  <Field label="Full payment due by">
                    <TextField
                      type="date"
                      fullWidth
                      size="small"
                      value={createForm.pay_by}
                      onChange={(e) =>
                        setCreateForm((p) => ({ ...p, pay_by: e.target.value }))
                      }
                      sx={inputSx(s)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Field>
                  <Field label="Expected partial shipments">
                    <TextField
                      type="number"
                      fullWidth
                      size="small"
                      value={createForm.expected_parts}
                      onChange={(e) =>
                        setCreateForm((p) => ({
                          ...p,
                          expected_parts: e.target.value,
                        }))
                      }
                      sx={inputSx(s)}
                    />
                  </Field>
                </>
              )}
              {createForm.payment_model === "preorder" && (
                <>
                  <Field label="Expected availability">
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="e.g. CW 45"
                      value={createForm.expected_availability}
                      onChange={(e) =>
                        setCreateForm((p) => ({
                          ...p,
                          expected_availability: e.target.value,
                        }))
                      }
                      sx={inputSx(s)}
                    />
                  </Field>
                  <Field label="Price">
                    <TextField
                      select
                      fullWidth
                      size="small"
                      value={createForm.price_mode}
                      onChange={(e) =>
                        setCreateForm((p) => ({
                          ...p,
                          price_mode: e.target.value,
                        }))
                      }
                      sx={inputSx(s)}
                    >
                      <MenuItem value="fixed">Fixed price</MenuItem>
                      <MenuItem value="spot">
                        Spot price on availability
                      </MenuItem>
                    </TextField>
                  </Field>
                </>
              )}
              {createForm.payment_model === "net_terms" && (
                <Field label="Payment terms (days after goods receipt)">
                  <TextField
                    type="number"
                    fullWidth
                    size="small"
                    value={createForm.payment_term_days}
                    onChange={(e) =>
                      setCreateForm((p) => ({
                        ...p,
                        payment_term_days: e.target.value,
                      }))
                    }
                    sx={inputSx(s)}
                  />
                </Field>
              )}
            </Box>

            <Box
              sx={{
                mt: 1.75,
                background: s.dark ? "#101a33" : "#f0f4fb",
                borderRadius: "8px",
                p: 1.5,
              }}
            >
              <Typography
                sx={{
                  fontSize: 11.5,
                  textTransform: "uppercase",
                  letterSpacing: 0.6,
                  color: s.muted,
                  fontWeight: 600,
                  mb: 0.75,
                }}
              >
                Payment plan (preview)
              </Typography>
              {planPreview.map(([label, when, amt]) => (
                <Stack
                  key={label}
                  direction="row"
                  justifyContent="space-between"
                  gap={1.5}
                  sx={{
                    py: 0.85,
                    borderBottom: `1px dashed ${s.border}`,
                    "&:last-child": { borderBottom: "none" },
                    fontSize: 13,
                  }}
                >
                  <Typography sx={{ fontSize: 13 }}>
                    {label}{" "}
                    <Box component="span" sx={{ color: s.muted }}>
                      · {when}
                    </Box>
                  </Typography>
                  <Typography
                    sx={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}
                  >
                    {amt}
                  </Typography>
                </Stack>
              ))}
            </Box>
          </Box>

          <SectionLabel s={s}>Delivery</SectionLabel>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 1.5,
            }}
          >
            <Field label="Deliver to">
              <TextField
                select
                fullWidth
                size="small"
                value={createForm.deliver_to}
                onChange={(e) =>
                  setCreateForm((p) => ({ ...p, deliver_to: e.target.value }))
                }
                sx={inputSx(s)}
              >
                {DELIVER_TO.map((d) => (
                  <MenuItem key={d.value} value={d.value}>
                    {d.label}
                  </MenuItem>
                ))}
              </TextField>
            </Field>
            <Field label="Shipping method">
              <TextField
                select
                fullWidth
                size="small"
                value={createForm.ship_method}
                onChange={(e) =>
                  setCreateForm((p) => ({ ...p, ship_method: e.target.value }))
                }
                sx={inputSx(s)}
              >
                {SHIP_METHODS.map((m) => (
                  <MenuItem key={m.value} value={m.value}>
                    {m.label}
                  </MenuItem>
                ))}
              </TextField>
            </Field>
            <Field label="Note" wide>
              <TextField
                fullWidth
                multiline
                minRows={2}
                size="small"
                placeholder="Anything to note for this order?"
                value={createForm.note}
                onChange={(e) =>
                  setCreateForm((p) => ({ ...p, note: e.target.value }))
                }
                sx={inputSx(s)}
              />
            </Field>
          </Box>

          <SectionLabel s={s}>Positions &amp; file</SectionLabel>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            alignItems={{ sm: "center" }}
            mb={1.5}
          >
            <Button variant="outlined" component="label" sx={outlineBtnSx(s)}>
              {parseFile ? parseFile.name : "Choose file"}
              <input
                type="file"
                hidden
                onChange={(e) => {
                  setParseFile(e.target.files?.[0] || null);
                  setPendingFileId(null);
                }}
              />
            </Button>
            <Button
              variant="outlined"
              onClick={handleParseFile}
              disabled={parsing || !parseFile || !createForm.wholesaler_id}
              sx={outlineBtnSx(s)}
            >
              {parsing ? "Parsing…" : "Parse with n8n"}
            </Button>
            <Button
              variant="outlined"
              onClick={() =>
                setCreatePositions((rows) => [...rows, emptyPosition()])
              }
              sx={outlineBtnSx(s)}
            >
              Add row
            </Button>
            {pendingFileId && (
              <Typography sx={{ fontSize: 12, color: s.muted }}>
                File stored — will attach on create
              </Typography>
            )}
          </Stack>
          {createPositions.length === 0 ? (
            <Typography sx={{ fontSize: 12.5, color: s.muted, mb: 1 }}>
              No positions yet. Parse a file or add a row.
            </Typography>
          ) : (
            <Stack spacing={0.75} mb={1} sx={{ overflowX: "auto" }}>
              <Box
                sx={{
                  display: { xs: "none", sm: "grid" },
                  gridTemplateColumns:
                    "minmax(140px,1.4fr) minmax(90px,1fr) 72px 88px 88px minmax(90px,0.9fr) minmax(100px,1fr) 36px",
                  gap: 0.75,
                  px: 0.25,
                  pb: 0.5,
                }}
              >
                {[
                  "Title",
                  "EAN",
                  "Qty",
                  "Unit price",
                  "Line total",
                  "Carrier",
                  "Tracking",
                  "",
                ].map((label) => (
                  <Typography
                    key={label || "actions"}
                    sx={{
                      fontSize: 11,
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      color: s.muted,
                    }}
                  >
                    {label}
                  </Typography>
                ))}
              </Box>
              {createPositions.map((row, idx) => {
                const qty = Number(row.qty_ordered);
                const price = Number(row.unit_price);
                const lineTotal =
                  Number.isFinite(qty) &&
                  Number.isFinite(price) &&
                  qty >= 0 &&
                  price >= 0
                    ? qty * price
                    : null;
                return (
                  <Box
                    key={idx}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "minmax(140px,1.4fr) minmax(90px,1fr) 72px 88px 88px minmax(90px,0.9fr) minmax(100px,1fr) 36px",
                      },
                      gap: 0.75,
                      alignItems: "center",
                    }}
                  >
                    <TextField
                      size="small"
                      placeholder="Title *"
                      value={row.title}
                      onChange={(e) =>
                        setCreatePositions((rows) =>
                          rows.map((r, i) =>
                            i === idx ? { ...r, title: e.target.value } : r,
                          ),
                        )
                      }
                      sx={inputSx(s)}
                    />
                    <TextField
                      size="small"
                      placeholder="EAN"
                      value={row.ean}
                      onChange={(e) =>
                        setCreatePositions((rows) =>
                          rows.map((r, i) =>
                            i === idx ? { ...r, ean: e.target.value } : r,
                          ),
                        )
                      }
                      sx={inputSx(s)}
                    />
                    <TextField
                      size="small"
                      type="number"
                      placeholder="Qty"
                      value={row.qty_ordered}
                      onChange={(e) =>
                        setCreatePositions((rows) =>
                          rows.map((r, i) =>
                            i === idx
                              ? { ...r, qty_ordered: e.target.value }
                              : r,
                          ),
                        )
                      }
                      sx={inputSx(s)}
                    />
                    <TextField
                      size="small"
                      type="number"
                      placeholder="Price"
                      value={row.unit_price}
                      onChange={(e) =>
                        setCreatePositions((rows) =>
                          rows.map((r, i) =>
                            i === idx
                              ? { ...r, unit_price: e.target.value }
                              : r,
                          ),
                        )
                      }
                      sx={inputSx(s)}
                    />
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontVariantNumeric: "tabular-nums",
                        color: s.text,
                        px: 0.5,
                      }}
                    >
                      {lineTotal != null ? formatMoney(lineTotal, "EUR") : "—"}
                    </Typography>
                    <TextField
                      select
                      size="small"
                      value={row.carrier || ""}
                      onChange={(e) =>
                        setCreatePositions((rows) =>
                          rows.map((r, i) =>
                            i === idx ? { ...r, carrier: e.target.value } : r,
                          ),
                        )
                      }
                      sx={inputSx(s)}
                    >
                      <MenuItem value="">—</MenuItem>
                      {CARRIERS.map((carr) => (
                        <MenuItem key={carr.value} value={carr.value}>
                          {carr.label}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      size="small"
                      placeholder="Tracking"
                      value={row.tracking_no}
                      onChange={(e) =>
                        setCreatePositions((rows) =>
                          rows.map((r, i) =>
                            i === idx
                              ? { ...r, tracking_no: e.target.value }
                              : r,
                          ),
                        )
                      }
                      sx={inputSx(s)}
                    />
                    <IconButton
                      size="small"
                      onClick={() =>
                        setCreatePositions((rows) =>
                          rows.filter((_, i) => i !== idx),
                        )
                      }
                      sx={{ color: s.muted }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                );
              })}
            </Stack>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            px: 2.75,
            py: 1.75,
            borderTop: `1px solid ${s.theadBorder}`,
            bgcolor: s.footBg,
          }}
        >
          <Button
            onClick={() => setCreateOpen(false)}
            sx={{
              ...outlineBtnSx(s),
              border: "none",
              bgcolor: "transparent",
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={saving || !wholesalers.length}
            sx={primaryBtnSx(s)}
          >
            Create order
          </Button>
        </DialogActions>
      </Dialog>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setEditingPaymentId(null);
        }}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 560 },
            bgcolor: s.card,
            borderLeft: `1px solid ${s.line}`,
          },
        }}
      >
        {selected && c && (
          <Box
            sx={{ display: "flex", flexDirection: "column", height: "100%" }}
          >
            <Box sx={{ p: 2.75, pb: 0, position: "relative" }}>
              <IconButton
                onClick={() => setDrawerOpen(false)}
                sx={{ position: "absolute", top: 8, right: 8, color: s.muted }}
              >
                <CloseIcon />
              </IconButton>
              <Typography sx={{ fontSize: 20, fontWeight: 600, pr: 5 }}>
                {selected.wholesaler?.company_name} · {selected.order_no}
              </Typography>
              <Stack
                direction="row"
                spacing={1}
                mt={1}
                flexWrap="wrap"
                useFlexGap
                alignItems="center"
              >
                <Pill tone={PHASES[c.phase]?.pill || "grey"}>
                  {c.phase_label}
                </Pill>
                <Pill
                  tone={PAYMENT_MODELS[selected.payment_model]?.pill || "grey"}
                >
                  {PAYMENT_MODELS[selected.payment_model]?.short}
                </Pill>
                <Typography sx={{ fontSize: 12.5, color: s.muted }}>
                  ordered {formatDate(selected.order_date)}
                  {selected.wholesaler?.country
                    ? ` · ${selected.wholesaler.country}`
                    : ""}
                </Typography>
              </Stack>
              <Tabs
                value={drawerTab}
                onChange={(_, v) => setDrawerTab(v)}
                variant="scrollable"
                sx={{
                  mt: 2,
                  minHeight: 42,
                  borderBottom: `1px solid ${s.theadBorder}`,
                  "& .MuiTabs-indicator": {
                    backgroundColor: "#4b84f5",
                    height: 2,
                  },
                }}
              >
                <Tab label="Overview" sx={drawerTabSx} />
                <Tab
                  label={`Payments (${selected.payments?.length || 0})`}
                  sx={drawerTabSx}
                />
                <Tab
                  label={`Positions (${selected.items?.length || 0})`}
                  sx={drawerTabSx}
                />
                <Tab label="Timeline" sx={drawerTabSx} />
              </Tabs>
            </Box>
            <Box sx={{ p: 2.75, flex: 1, overflow: "auto" }}>
              {drawerTab === 0 && (
                <Stack spacing={1.5}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 1.2,
                    }}
                  >
                    {[
                      {
                        key: "order-net",
                        label: "Order value net",
                        value: formatMoney(
                          selected.total_net,
                          selected.currency,
                        ),
                        sub: c.vat_applied
                          ? `To transfer: ${formatMoney(c.payable_total, selected.currency)} (net + 19% VAT)`
                          : `${selected.units_total} units`,
                        emphasize: true,
                      },
                      {
                        key: "paid",
                        label: "Paid",
                        value: formatMoney(c.paid, selected.currency),
                        sub: `${c.paid_pct}%${c.vat_applied ? " of amount to transfer" : ""}`,
                        emphasize: false,
                      },
                      {
                        key: "still-to-pay",
                        label: "Still to pay",
                        value: formatMoney(c.unpaid, selected.currency),
                        sub:
                          c.phase === "payment_due" && Number(c.due_amount) > 0
                            ? `Due now: ${formatMoney(c.due_amount, selected.currency)}${c.vat_applied ? " incl. VAT" : ""}`
                            : Number(c.unpaid) > 0
                              ? "Not due yet"
                              : "Nothing left",
                        emphasize: true,
                        warn: Number(c.unpaid) > 0,
                      },
                      {
                        key: "goods",
                        label: "Goods received",
                        value: formatMoney(
                          c.delivered_value,
                          selected.currency,
                        ),
                        sub: `${c.units_in} of ${selected.units_total} units`,
                        emphasize: false,
                      },
                    ].map((card) => (
                      <Box
                        key={card.key}
                        sx={{
                          background: s.boxBg,
                          borderRadius: "10px",
                          border: `1px solid ${
                            card.warn ? (s.warnBorder || "#e0a800") : s.border
                          }`,
                          p: 1.5,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 11.5,
                            color: s.muted,
                            fontWeight: 600,
                            textTransform: "uppercase",
                            letterSpacing: 0.6,
                          }}
                        >
                          {card.label}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: card.emphasize ? 22 : 20,
                            fontWeight: card.emphasize ? 700 : 600,
                            mt: 0.5,
                            fontVariantNumeric: "tabular-nums",
                            color: card.warn ? (s.warnText || undefined) : undefined,
                          }}
                        >
                          {card.value}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: s.muted, mt: 0.25 }}>
                          {card.sub}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                  <Typography sx={{ fontSize: 13.5, color: s.nextStep }}>
                    {c.next_step}
                  </Typography>
                  {(selected.payment_model === "deposit" ||
                    selected.payment_model === "preorder") &&
                    !selected.goods_ready && (
                      <Button
                        variant="outlined"
                        onClick={async () => {
                          await wholesaleOrdersApi.markReady(selected.id);
                          setSuccess("Marked goods available");
                          await refreshSelected(selected.id);
                        }}
                        sx={outlineBtnSx(s)}
                      >
                        Mark goods available at wholesaler
                      </Button>
                    )}
                  <Divider sx={{ borderColor: s.theadBorder }} />
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: s.muted,
                      textTransform: "uppercase",
                      letterSpacing: 0.6,
                    }}
                  >
                    Order details
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 1.25,
                    }}
                  >
                    <TextField
                      select
                      size="small"
                      label="Owner"
                      value={metaForm.owner}
                      onChange={(e) =>
                        setMetaForm((f) => ({ ...f, owner: e.target.value }))
                      }
                      sx={inputSx(s)}
                    >
                      <MenuItem value="">—</MenuItem>
                      {users.map((u) => {
                        const name = userDisplayName(u);
                        return (
                          <MenuItem key={u.id || name} value={name}>
                            {name}
                          </MenuItem>
                        );
                      })}
                    </TextField>
                    <TextField
                      select
                      size="small"
                      label="Deliver to"
                      value={metaForm.deliver_to}
                      onChange={(e) =>
                        setMetaForm((f) => ({
                          ...f,
                          deliver_to: e.target.value,
                        }))
                      }
                      sx={inputSx(s)}
                    >
                      {DELIVER_TO.map((d) => (
                        <MenuItem key={d.value} value={d.value}>
                          {d.label}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      size="small"
                      label="Invoice / proforma"
                      value={metaForm.invoice_no}
                      onChange={(e) =>
                        setMetaForm((f) => ({
                          ...f,
                          invoice_no: e.target.value,
                        }))
                      }
                      sx={inputSx(s)}
                    />
                    <TextField
                      size="small"
                      label="Expected availability"
                      value={metaForm.expected_availability}
                      onChange={(e) =>
                        setMetaForm((f) => ({
                          ...f,
                          expected_availability: e.target.value,
                        }))
                      }
                      placeholder="e.g. CW 42 / mid November"
                      sx={inputSx(s)}
                    />
                    <TextField
                      size="small"
                      label="Pay by"
                      type="date"
                      value={metaForm.pay_by}
                      onChange={(e) =>
                        setMetaForm((f) => ({ ...f, pay_by: e.target.value }))
                      }
                      InputLabelProps={{ shrink: true }}
                      sx={inputSx(s)}
                    />
                    <TextField
                      size="small"
                      label="Note"
                      value={metaForm.note}
                      onChange={(e) =>
                        setMetaForm((f) => ({ ...f, note: e.target.value }))
                      }
                      multiline
                      minRows={2}
                      sx={{ ...inputSx(s), gridColumn: "1 / -1" }}
                    />
                  </Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Button
                      variant="contained"
                      onClick={handleSaveMeta}
                      disabled={savingMeta}
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        bgcolor: s.blue,
                        "&:hover": { bgcolor: "#2459d1" },
                      }}
                    >
                      {savingMeta ? "Saving…" : "Save details"}
                    </Button>
                    {selected.has_invoice_file && (
                      <Button
                        variant="outlined"
                        onClick={handleDownloadInvoice}
                        sx={outlineBtnSx(s)}
                      >
                        Download invoice file
                      </Button>
                    )}
                  </Stack>
                </Stack>
              )}

              {drawerTab === 1 && (
                <Stack spacing={1.5}>
                  {(selected.payments || []).length === 0 ? (
                    <Box
                      sx={{
                        background: s.boxBg,
                        borderRadius: "10px",
                        border: `1px solid ${s.border}`,
                        p: 1.5,
                        color: s.muted,
                      }}
                    >
                      No payments recorded yet.
                    </Box>
                  ) : (
                    selected.payments.map((p) => (
                      <Box
                        key={p.id}
                        sx={{
                          background: s.boxBg,
                          borderRadius: "10px",
                          p: 1.5,
                          border: `1px solid ${s.border}`,
                        }}
                      >
                        {editingPaymentId === p.id ? (
                          <Stack spacing={1}>
                            <Box
                              sx={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: 1,
                              }}
                            >
                              <TextField
                                size="small"
                                label="Amount"
                                type="number"
                                value={payEditForm.amount}
                                onChange={(e) =>
                                  setPayEditForm((f) => ({
                                    ...f,
                                    amount: e.target.value,
                                  }))
                                }
                                sx={inputSx(s)}
                              />
                              <TextField
                                select
                                size="small"
                                label="Kind"
                                value={payEditForm.kind}
                                onChange={(e) =>
                                  setPayEditForm((f) => ({
                                    ...f,
                                    kind: e.target.value,
                                  }))
                                }
                                sx={inputSx(s)}
                              >
                                {PAYMENT_KINDS.map((k) => (
                                  <MenuItem key={k.value} value={k.value}>
                                    {k.label}
                                  </MenuItem>
                                ))}
                              </TextField>
                              <TextField
                                type="date"
                                size="small"
                                label="Date"
                                InputLabelProps={{ shrink: true }}
                                value={payEditForm.paid_at}
                                onChange={(e) =>
                                  setPayEditForm((f) => ({
                                    ...f,
                                    paid_at: e.target.value,
                                  }))
                                }
                                sx={inputSx(s)}
                              />
                              <TextField
                                select
                                size="small"
                                label="Method"
                                value={payEditForm.method}
                                onChange={(e) =>
                                  setPayEditForm((f) => ({
                                    ...f,
                                    method: e.target.value,
                                  }))
                                }
                                sx={inputSx(s)}
                              >
                                {PAYMENT_METHODS.map((m) => (
                                  <MenuItem key={m.value} value={m.value}>
                                    {m.label}
                                  </MenuItem>
                                ))}
                              </TextField>
                              <TextField
                                size="small"
                                label="Reference"
                                value={payEditForm.reference}
                                onChange={(e) =>
                                  setPayEditForm((f) => ({
                                    ...f,
                                    reference: e.target.value,
                                  }))
                                }
                                sx={{ ...inputSx(s), gridColumn: "1 / -1" }}
                              />
                            </Box>
                            <Stack direction="row" spacing={1}>
                              <Button
                                size="small"
                                variant="contained"
                                sx={primaryBtnSx(s)}
                                onClick={handleUpdatePayment}
                              >
                                Save
                              </Button>
                              <Button
                                size="small"
                                sx={outlineBtnSx(s)}
                                onClick={() => setEditingPaymentId(null)}
                              >
                                Cancel
                              </Button>
                            </Stack>
                          </Stack>
                        ) : (
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="flex-start"
                            gap={1}
                          >
                            <Box>
                              <Typography sx={{ fontWeight: 600 }}>
                                {PAYMENT_KINDS.find((k) => k.value === p.kind)
                                  ?.label || p.kind}
                              </Typography>
                              <Typography sx={{ fontSize: 12, color: s.muted }}>
                                {formatDate(p.paid_at)} ·{" "}
                                {PAYMENT_METHODS.find(
                                  (m) => m.value === p.method,
                                )?.label || p.method}
                                {p.reference ? ` · ${p.reference}` : ""}
                              </Typography>
                            </Box>
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={0.5}
                            >
                              <Typography
                                sx={{
                                  fontWeight: 600,
                                  fontVariantNumeric: "tabular-nums",
                                  mr: 0.5,
                                }}
                              >
                                {formatMoney(p.amount, selected.currency)}
                              </Typography>
                              <Button
                                size="small"
                                sx={{
                                  ...outlineBtnSx(s),
                                  minHeight: 28,
                                  fontSize: 11,
                                  px: 1,
                                }}
                                onClick={() => startEditPayment(p)}
                              >
                                Edit
                              </Button>
                              <IconButton
                                size="small"
                                aria-label="Delete payment"
                                onClick={() => handleDeletePayment(p.id)}
                                sx={{ color: s.muted }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          </Stack>
                        )}
                      </Box>
                    ))
                  )}
                  <Box
                    sx={{
                      background: s.accessBg,
                      border: `1px solid ${s.accessBorder}`,
                      borderRadius: "10px",
                      p: 1.5,
                    }}
                  >
                    <Typography sx={{ fontWeight: 600, mb: 1, fontSize: 13 }}>
                      Record payment
                    </Typography>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 1,
                      }}
                    >
                      <TextField
                        size="small"
                        label="Amount"
                        type="number"
                        value={payForm.amount}
                        onChange={(e) =>
                          setPayForm((p) => ({ ...p, amount: e.target.value }))
                        }
                        sx={inputSx(s)}
                      />
                      <TextField
                        select
                        size="small"
                        label="Kind"
                        value={payForm.kind}
                        onChange={(e) =>
                          setPayForm((p) => ({ ...p, kind: e.target.value }))
                        }
                        sx={inputSx(s)}
                      >
                        {PAYMENT_KINDS.map((k) => (
                          <MenuItem key={k.value} value={k.value}>
                            {k.label}
                          </MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        type="date"
                        size="small"
                        label="Date"
                        InputLabelProps={{ shrink: true }}
                        value={payForm.paid_at}
                        onChange={(e) =>
                          setPayForm((p) => ({ ...p, paid_at: e.target.value }))
                        }
                        sx={inputSx(s)}
                      />
                      <TextField
                        select
                        size="small"
                        label="Method"
                        value={payForm.method}
                        onChange={(e) =>
                          setPayForm((p) => ({ ...p, method: e.target.value }))
                        }
                        sx={inputSx(s)}
                      >
                        {PAYMENT_METHODS.map((m) => (
                          <MenuItem key={m.value} value={m.value}>
                            {m.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Box>
                    <Button
                      variant="contained"
                      sx={{ mt: 1.5, ...primaryBtnSx(s) }}
                      onClick={handleAddPayment}
                    >
                      Save payment
                    </Button>
                  </Box>
                </Stack>
              )}

              {drawerTab === 2 && (
                <Stack spacing={1.5}>
                  {(selected.items || []).length === 0 ? (
                    <Box
                      sx={{
                        background: s.boxBg,
                        borderRadius: "10px",
                        border: `1px solid ${s.border}`,
                        p: 1.5,
                        color: s.muted,
                      }}
                    >
                      No positions yet. Add rows or parse a file when creating
                      the order.
                    </Box>
                  ) : (
                    selected.items.map((it) => (
                      <Box
                        key={it.id}
                        sx={{
                          background: s.boxBg,
                          borderRadius: "10px",
                          p: 1.5,
                          border: `1px solid ${s.border}`,
                        }}
                      >
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                          gap={1}
                          mb={1}
                        >
                          <Pill tone={shipStatusPill(it.status)}>
                            {SHIP_STATUSES.find((x) => x.value === it.status)
                              ?.label || it.status}
                          </Pill>
                          <Stack
                            direction="row"
                            spacing={0.5}
                            alignItems="center"
                          >
                            {it.tracking_url ? (
                              <Link
                                href={it.tracking_url}
                                target="_blank"
                                rel="noreferrer"
                                sx={{ fontSize: 12 }}
                              >
                                Open tracking
                              </Link>
                            ) : null}
                            <IconButton
                              size="small"
                              aria-label="Delete position"
                              onClick={() => handleDeleteItem(it.id, it.title)}
                              sx={{ color: s.muted }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        </Stack>
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: 1,
                          }}
                        >
                          <TextField
                            size="small"
                            label="Title"
                            defaultValue={it.title || ""}
                            key={`title-${it.id}-${it.title}`}
                            onBlur={(e) => {
                              const val = e.target.value.trim();
                              if (!val || val === (it.title || "")) return;
                              patchItem(it.id, { title: val });
                            }}
                            sx={{ ...inputSx(s), gridColumn: "1 / -1" }}
                          />
                          <TextField
                            size="small"
                            label="EAN"
                            defaultValue={it.ean || ""}
                            key={`ean-${it.id}-${it.ean || ""}`}
                            onBlur={(e) => {
                              const val = e.target.value.trim();
                              if (val === (it.ean || "")) return;
                              patchItem(it.id, { ean: val || null });
                            }}
                            sx={inputSx(s)}
                          />
                          <TextField
                            size="small"
                            type="number"
                            label="Unit price"
                            defaultValue={
                              it.unit_price != null ? String(it.unit_price) : ""
                            }
                            key={`up-${it.id}-${it.unit_price ?? ""}`}
                            onBlur={(e) => {
                              const raw = e.target.value.trim();
                              const next = raw === "" ? null : Number(raw);
                              const prev =
                                it.unit_price != null
                                  ? Number(it.unit_price)
                                  : null;
                              if (next === prev) return;
                              if (raw !== "" && !(Number(raw) >= 0)) return;
                              patchItem(it.id, { unit_price: next });
                            }}
                            sx={inputSx(s)}
                          />
                          <TextField
                            size="small"
                            type="number"
                            label="Qty ordered"
                            defaultValue={it.qty_ordered ?? 0}
                            key={`qo-${it.id}-${it.qty_ordered}`}
                            onBlur={(e) => {
                              const next = Number(e.target.value) || 0;
                              if (next === (it.qty_ordered || 0)) return;
                              patchItem(it.id, {
                                qty_ordered: next,
                                units: next,
                              });
                            }}
                            sx={inputSx(s)}
                          />
                          <TextField
                            size="small"
                            type="number"
                            label="Qty received"
                            defaultValue={it.qty_received ?? 0}
                            key={`qr-${it.id}-${it.qty_received}`}
                            onBlur={(e) => {
                              const next = Number(e.target.value) || 0;
                              if (next === (it.qty_received || 0)) return;
                              patchItem(it.id, { qty_received: next });
                            }}
                            sx={inputSx(s)}
                          />
                          <TextField
                            select
                            size="small"
                            label="Status"
                            value={it.status || "pending"}
                            onChange={(e) =>
                              patchItem(it.id, { status: e.target.value })
                            }
                            sx={inputSx(s)}
                          >
                            {SHIP_STATUSES.map((st) => (
                              <MenuItem key={st.value} value={st.value}>
                                {st.label}
                              </MenuItem>
                            ))}
                          </TextField>
                          <TextField
                            select
                            size="small"
                            label="Carrier"
                            value={it.carrier || ""}
                            onChange={(e) =>
                              patchItem(it.id, {
                                carrier: e.target.value || null,
                              })
                            }
                            sx={inputSx(s)}
                          >
                            <MenuItem value="">—</MenuItem>
                            {CARRIERS.map((carr) => (
                              <MenuItem key={carr.value} value={carr.value}>
                                {carr.label}
                              </MenuItem>
                            ))}
                          </TextField>
                          <TextField
                            size="small"
                            label="Tracking"
                            defaultValue={it.tracking_no || ""}
                            key={`tr-${it.id}-${it.tracking_no || ""}`}
                            onBlur={(e) => {
                              const val = e.target.value.trim();
                              if (val === (it.tracking_no || "")) return;
                              patchItem(it.id, {
                                tracking_no: val || null,
                              });
                            }}
                            sx={{ ...inputSx(s), gridColumn: "1 / -1" }}
                          />
                        </Box>
                        {it.status !== "delivered" && (
                          <Button
                            size="small"
                            sx={{
                              mt: 1,
                              ...outlineBtnSx(s),
                              minHeight: 36,
                              fontSize: 12,
                            }}
                            onClick={async () => {
                              await wholesaleOrdersApi.markItemDelivered(
                                selected.id,
                                it.id,
                              );
                              setSuccess("Marked delivered");
                              await refreshSelected(selected.id);
                            }}
                          >
                            Mark delivered
                          </Button>
                        )}
                      </Box>
                    ))
                  )}
                  <Box
                    sx={{
                      background: s.accessBg,
                      border: `1px solid ${s.accessBorder}`,
                      borderRadius: "10px",
                      p: 1.5,
                    }}
                  >
                    <Typography sx={{ fontWeight: 600, mb: 1, fontSize: 13 }}>
                      Add position
                    </Typography>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 1,
                      }}
                    >
                      <TextField
                        size="small"
                        label="Title *"
                        value={itemForm.title}
                        onChange={(e) =>
                          setItemForm((p) => ({ ...p, title: e.target.value }))
                        }
                        sx={{ ...inputSx(s), gridColumn: "1 / -1" }}
                      />
                      <TextField
                        size="small"
                        label="EAN"
                        value={itemForm.ean}
                        onChange={(e) =>
                          setItemForm((p) => ({ ...p, ean: e.target.value }))
                        }
                        sx={inputSx(s)}
                      />
                      <TextField
                        size="small"
                        type="number"
                        label="Qty ordered"
                        value={itemForm.qty_ordered}
                        onChange={(e) =>
                          setItemForm((p) => ({
                            ...p,
                            qty_ordered: e.target.value,
                          }))
                        }
                        sx={inputSx(s)}
                      />
                      <TextField
                        select
                        size="small"
                        label="Carrier"
                        value={itemForm.carrier}
                        onChange={(e) =>
                          setItemForm((p) => ({
                            ...p,
                            carrier: e.target.value,
                          }))
                        }
                        sx={inputSx(s)}
                      >
                        <MenuItem value="">—</MenuItem>
                        {CARRIERS.map((carr) => (
                          <MenuItem key={carr.value} value={carr.value}>
                            {carr.label}
                          </MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        size="small"
                        label="Tracking no."
                        value={itemForm.tracking_no}
                        onChange={(e) =>
                          setItemForm((p) => ({
                            ...p,
                            tracking_no: e.target.value,
                          }))
                        }
                        sx={inputSx(s)}
                      />
                    </Box>
                    <Button
                      variant="contained"
                      sx={{ mt: 1.5, ...primaryBtnSx(s) }}
                      onClick={handleAddItem}
                    >
                      Save position
                    </Button>
                  </Box>
                </Stack>
              )}

              {drawerTab === 3 && (
                <Stack spacing={0}>
                  {(selected.events || []).map((ev) => (
                    <Stack
                      key={ev.id}
                      direction="row"
                      spacing={1.5}
                      sx={{ borderBottom: `1px dashed ${s.border}`, py: 1 }}
                    >
                      <Typography
                        sx={{
                          fontSize: 12,
                          color: s.muted,
                          minWidth: 90,
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {formatDate(ev.at)}
                      </Typography>
                      <Typography sx={{ fontSize: 13 }}>{ev.text}</Typography>
                    </Stack>
                  ))}
                </Stack>
              )}
            </Box>
            <Box
              sx={{
                p: 1.75,
                borderTop: `1px solid ${s.theadBorder}`,
                background: s.footBg,
                display: "flex",
                justifyContent: "space-between",
                gap: 1,
              }}
            >
              <Button
                color="error"
                variant="outlined"
                disabled={deleting}
                onClick={handleDelete}
                sx={{
                  ...outlineBtnSx(s),
                  borderColor: s.dark ? "#7a2a31" : "#f0b0aa",
                  color: s.dark ? "#ffaaa2" : "#a32a22",
                  "&:hover": {
                    borderColor: s.dark ? "#a03a42" : "#e09088",
                    background: s.dark ? "#45161b" : "#ffeceb",
                  },
                }}
              >
                {deleting ? "Deleting…" : "Delete order"}
              </Button>
              <Button onClick={() => setDrawerOpen(false)} sx={outlineBtnSx(s)}>
                Close
              </Button>
            </Box>
          </Box>
        )}
      </Drawer>
    </Box>
  );
};

export default OrdersTab;
